import { useQuery } from '@tanstack/react-query';
import type { DutyCountsDto } from '@pams/shared';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C5 §11b/§11d] ONE OFFICER'S LAST THREE MONTHS — FETCHED ON HOVER, ONCE.
 *
 *  §11c is the constraint this hook exists to respect: [C3c §4b] deliberately kept the 90-day
 *  count off the search path, "because an aggregation per result makes every keystroke slow".
 *  So this asks for ONE officer, only once the pointer or the keyboard cursor lands on their
 *  row, and the batch endpoint the list already uses answers it through a flag — no new route,
 *  no Postman entry, and `requireFillSlot`'s 404-not-403 and `addressedUnitIds`' membership
 *  check both come for free.
 *
 *  ⚠️ `staleTime: Infinity` IS §11d. "Cached per officer for the life of the drawer" — a second
 *  hover over the same row must not re-fetch, and a duty count from ninety seconds ago is not a
 *  number anybody is acting on to the minute. The sheet unmounting is what ends the cache.
 *
 *  ⚠️ AND `enabled` IS WHAT MAKES THE HOVER A HOVER. Mounting this hook per row with no gate
 *  would be exactly the per-result aggregation §11c forbids; the sheet holds ONE instance and
 *  passes the officer the cursor is on, so at most one request is ever in flight.
 *
 *  ⚠️ IT ONLY EVER READS. `fillKeyIsolation.test.ts` forbids any invalidation at or above a live
 *  sheet, and this hook has no mutation and no `invalidateQueries` — its key is a new leaf under
 *  `queryKeys.fill`, which no existing invalidation reaches.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export type DutyMonthsState = {
  buckets: { month: string; count: number }[] | null;
  isPending: boolean;
  isError: boolean;
};

export const useDutyMonths = (input: {
  dutyId: string;
  slotRef: string;
  /** The row the pointer or the arrow-key cursor is on. `null` closes the request. */
  officerId: string | null;
}): DutyMonthsState => {
  const officerId = input.officerId ?? '';

  const result = useQuery({
    queryKey: queryKeys.fill.dutyMonths(input.dutyId, input.slotRef, officerId),
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      api.get<DutyCountsDto>(`/duties/${input.dutyId}/duty-counts`, {
        /* ⚠️ ONE id, and `breakdown` is what makes the response carry `months` at all. */
        query: { slotRef: input.slotRef, officerIds: officerId, breakdown: 'true' },
        signal,
      }),
    enabled: officerId.length > 0,
    staleTime: Infinity,
  });

  /*
   * ⚠️ AN OFFICER THE CALLER MAY NOT ASK ABOUT IS SIMPLY ABSENT from the answer — the endpoint's
   * own rule, never an error and never a zero — so a missing row reads as `null` here and the
   * popover says it could not load rather than inventing three zeros.
   */
  const row = result.data?.months?.find((entry) => entry.officerId === officerId);
  return {
    buckets: row ? row.buckets : null,
    isPending: result.isPending && officerId.length > 0,
    isError: result.isError,
  };
};
